import React, { useState, useEffect } from "react";
import { createPageUrl } from "@/utils";
import { Link } from "react-router-dom";
import { db, uploadFile } from "@/api/db";
import { Plus, Edit, Eye, EyeOff, BookOpen, Play, Trash2, ImagePlus, X, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminTracks() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", category: "Transição", image_url: "", order: 1, is_published: false });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { loadTracks(); }, []);

  async function loadTracks() {
    try {
      const all = await db.entities.CareerTrack.list("-created_date");
      all.sort((a, b) => (a.order || 999) - (b.order || 999));
      setTracks(all);
    } catch (e) {
      console.error("Erro ao carregar módulos:", e);
    }
    setLoading(false);
  }

  function openCreate() {
    setEditing(null);
    setForm({ title: "", description: "", category: "Transição", image_url: "", order: tracks.length + 1, is_published: false });
    setShowForm(true);
  }

  function openEdit(track) {
    setEditing(track);
    setForm({ title: track.title || "", description: track.description || "", category: track.category || "Transição", image_url: track.image_url || "", order: track.order || 1, is_published: !!track.is_published });
    setShowForm(true);
  }

  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file, 'portfolio-assets');
      setForm(f => ({ ...f, image_url: url }));
    } catch (err) {
      console.error('Upload failed:', err);
    }
    setUploading(false);
  }

  async function moveTrack(track, direction) {
    const idx = tracks.findIndex(t => t.id === track.id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= tracks.length) return;
    const other = tracks[swapIdx];
    await Promise.all([
      db.entities.CareerTrack.update(track.id, { order: other.order || swapIdx + 1 }),
      db.entities.CareerTrack.update(other.id, { order: track.order || idx + 1 }),
    ]);
    await loadTracks();
  }

  async function handleSave() {
    setSaving(true);
    const data = { ...form, order: Number(form.order) };
    if (editing) {
      await db.entities.CareerTrack.update(editing.id, data);
    } else {
      await db.entities.CareerTrack.create(data);
    }
    await loadTracks();
    setShowForm(false);
    setSaving(false);
  }

  async function togglePublish(track) {
    await db.entities.CareerTrack.update(track.id, { is_published: !track.is_published });
    await loadTracks();
  }

  async function deleteTrack(track) {
    if (!window.confirm(`Excluir o módulo "${track.title}"?`)) return;
    await db.entities.CareerTrack.delete(track.id);
    await loadTracks();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciar Módulos</h1>
          <p className="text-gray-500 mt-1">{tracks.length} módulo(s) cadastrado(s)</p>
        </div>
        <Button onClick={openCreate} className="bg-emerald-500 hover:bg-emerald-600 gap-2">
          <Plus className="w-4 h-4" /> Novo Módulo
        </Button>
      </div>

      {tracks.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-16 text-center text-gray-400">
            <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>Nenhum módulo criado ainda.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tracks.map((track, idx) => (
            <Card key={track.id} className="border-0 shadow-sm">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={idx === 0}
                    onClick={() => moveTrack(track, -1)}>
                    <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={idx === tracks.length - 1}
                    onClick={() => moveTrack(track, 1)}>
                    <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                  </Button>
                </div>
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900">{track.title}</p>
                    <Badge variant="outline" className="text-xs">Trilha: {track.category}</Badge>
                    <Badge className={`text-xs border-0 ${track.is_published ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                      {track.is_published ? "Publicado" : "Rascunho"}
                    </Badge>
                  </div>
                  <p className="text-gray-400 text-sm truncate mt-0.5">{track.description}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Link to={createPageUrl("AdminLessons") + `?track_id=${track.id}`}>
                    <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs">
                      <Play className="w-3 h-3" /> Aulas
                    </Button>
                  </Link>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => togglePublish(track)}>
                    {track.is_published ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(track)}>
                    <Edit className="w-4 h-4 text-gray-400" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => deleteTrack(track)}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Módulo" : "Novo Módulo"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Título *</label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Desenvolvimento Full Stack" className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Descrição</label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Descreva o módulo..." className="mt-1 resize-none" rows={3} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Trilha</label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Transição", "Recolocação", "Promoção"].map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Foto de capa</label>
              {form.image_url ? (
                <div className="mt-1 relative rounded-xl overflow-hidden">
                  <img src={form.image_url} alt="Capa" className="w-full h-36 object-cover rounded-xl" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, image_url: "" }))}
                    className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="mt-1 flex flex-col items-center justify-center h-36 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-emerald-400 transition-colors">
                  {uploading ? (
                    <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <ImagePlus className="w-8 h-8 text-gray-300 mb-1" />
                      <span className="text-xs text-gray-400">Clique para enviar uma imagem</span>
                    </>
                  )}
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                </label>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">Cancelar</Button>
              <Button onClick={handleSave} disabled={!form.title || saving} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}